import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import Dialog from '@sorare/core/src/components/dialog';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { DailyClaim } from './DailyClaim';
import { FirstWeekClaim } from './FirstWeekClaim';
import {
  ClaimTicketQuery,
  ClaimTicketQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--triple-unit) 0;

  /* First child is the dialog close button */
  & > *:not(:last-child):not(:first-child) {
    border-bottom: 1px solid var(--c-neutral-400);
    padding-bottom: var(--triple-unit);
  }
`;
const OnCloseWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const DrawerContent = styled.div`
  text-align: center;
  overflow: hidden;
  height: 100%;
  justify-content: center;
  background-color: var(--c-neutral-200);
  border-radius: var(--triple-unit);
`;

const CLAIM_TICKET_QUERY = gql`
  query ClaimTicketQuery {
    football {
      rivals {
        id
        currentManager {
          id
          dailyClaimTasks {
            id
            ...FirstWeekClaim_footballManagerTask
          }
          ...DailyClaim_footballRivalsCurrentManager
        }
      }
    }
  }
  ${DailyClaim.fragments.footballRivalsCurrentManager}
  ${FirstWeekClaim.fragments.footballManagerTask}
` as TypedDocumentNode<ClaimTicketQuery, ClaimTicketQueryVariables>;

type ContentProps = { data?: ClaimTicketQuery };
const Content = ({ data }: ContentProps) => {
  const { currentManager } = data?.football.rivals || {};

  if (!currentManager) {
    return null;
  }

  return (
    <>
      {currentManager.dailyClaimTasks.length > 0 && (
        <FirstWeekClaim dailyClaimTasks={currentManager.dailyClaimTasks} />
      )}
      <DailyClaim currentManager={currentManager} />
    </>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
};
export const DailyClaimDialog = ({ open, onClose }: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');

  const { data, loading } = useQuery(CLAIM_TICKET_QUERY);

  if (isTabletOrAbove) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth hideHeader onClose={onClose}>
        <Skeleton loading={loading}>
          <Root gap={3}>
            <OnCloseWrapper>
              <CloseButton onClose={onClose} />
            </OnCloseWrapper>
            <Content data={data} />
          </Root>
        </Skeleton>
      </Dialog>
    );
  }

  return (
    <Drawer onBackdropClick={onClose} open={open} side="bottom">
      <OnCloseWrapper>
        <CloseButton onClose={onClose} />
      </OnCloseWrapper>
      <DrawerContent>
        <Root gap={3}>
          <Content data={data} />
        </Root>
      </DrawerContent>
    </Drawer>
  );
};
